import Request from '../Request';
import TopSellersApi, { DropdownOptions } from './TopSellers.service';

export type { DropdownOption, DropdownOptions } from './TopSellers.service';

export type AgingTierKey = 'fresh' | 'moderate' | 'warning' | 'critical';

/**
 * One SKU (size / color) of a style, at the latest inventory snapshot. Receive dates and
 * days_on_hand are null when the inventory feed has no receive date for the SKU.
 */
export interface AgedInventorySkuRow {
  sku_id: string;
  variant_title: string | null;
  size: string | null;
  color: string | null;
  units_oh: number;
  retail_value_oh: number;
  cost_value_oh: number;
  first_received: string | null; // YYYY-MM-DD
  last_received: string | null; // YYYY-MM-DD
  days_on_hand: number | null;
  // Units sold in the requested date range.
  units_sold: number;
}

/**
 * One style (product) rolled up from its SKUs: units and values are summed, first_received is the
 * earliest SKU receipt, last_received the latest, and days_on_hand the oldest SKU (the worst case
 * the style is ranked by). days_on_hand_min is the newest SKU, for the range under the badge.
 */
export interface AgedInventoryStyleRow {
  product_id: string;
  product_title: string;
  vendor: string | null;
  class_code: string | null;
  class_name: string | null;
  category: string | null;
  units_oh: number;
  retail_value_oh: number;
  cost_value_oh: number;
  first_received: string | null;
  last_received: string | null;
  days_on_hand: number | null;
  days_on_hand_min: number | null;
  // Units sold in the requested date range.
  units_sold: number;
  // No sale on record for any of the style's SKUs, ever (not just in the date range).
  never_sold: boolean;
  // From the catalog's product tags: true when any SKU is tagged core / continuous reorder.
  is_core: boolean | null;
  is_reorder: boolean | null;
  skus: AgedInventorySkuRow[];
}

export interface AgedInventoryApiResponse {
  rows: AgedInventoryStyleRow[];
  generated_at: string;
  currency: string;
  // Date (YYYY-MM-DD) ages are measured to; on-hand figures are the current inventory snapshot.
  as_of_date: string | null;
  // Set when the query failed but the endpoint still answered.
  query_error?: string;
}

export interface GetAgedInventoryParams {
  clientId: string | number;
  // Sales window (YYYY-MM-DD, inclusive) for units sold, sell-through and zero-sales.
  from: string;
  to: string;
  locationCodes?: string[];
  classCodes?: string[];
  vendorIds?: string[];
  categoryIds?: string[];
  // "Exclude selected instead": the list becomes NOT IN.
  excludeLocations?: boolean;
  excludeClasses?: boolean;
  excludeVendors?: boolean;
  excludeCategories?: boolean;
  // Minimum style age in days; null for all ages.
  ageThreshold?: number | null;
  agingTier?: AgingTierKey | null;
  // Only styles with no units sold in the date range.
  zeroSalesOnly?: boolean;
  excludeCore?: boolean;
}

/**
 * GET /api/reports/aged-inventory -- every style with units on hand in the client's current
 * inventory snapshot, with its SKUs nested. Ages are measured to as_of_date; units sold and
 * sell-through cover from..to. Throws when the request fails.
 */
function getAgedInventory(params: GetAgedInventoryParams): Promise<AgedInventoryApiResponse> {
  const {
    clientId,
    from,
    to,
    locationCodes,
    classCodes,
    vendorIds,
    categoryIds,
    excludeLocations,
    excludeClasses,
    excludeVendors,
    excludeCategories,
    ageThreshold,
    agingTier,
    zeroSalesOnly,
    excludeCore,
  } = params;

  return Request.get(
    '/api/reports/aged-inventory',
    {
      client_id: clientId,
      from,
      to,
      location_ids: locationCodes?.length ? locationCodes : undefined,
      class_ids: classCodes?.length ? classCodes : undefined,
      vendor_ids: vendorIds?.length ? vendorIds : undefined,
      category_ids: categoryIds?.length ? categoryIds : undefined,
      // Sent only with a list to exclude; an empty list filters nothing either way.
      exclude_locations: excludeLocations && locationCodes?.length ? 1 : undefined,
      exclude_classes: excludeClasses && classCodes?.length ? 1 : undefined,
      exclude_vendors: excludeVendors && vendorIds?.length ? 1 : undefined,
      exclude_categories: excludeCategories && categoryIds?.length ? 1 : undefined,
      age_threshold: ageThreshold ?? undefined,
      aging_tier: agingTier ?? undefined,
      zero_sales_only: zeroSalesOnly ? 1 : 0,
      exclude_core: excludeCore ? 1 : 0,
    },
    false,
    true
  );
}

/**
 * GET /api/reports/aged-inventory/vendors -- every vendor in the client's catalog, so the Vendor
 * filter offers the full list up front.
 */
async function getVendorOptions(clientId: string | number): Promise<string[]> {
  const res = await Request.get('/api/reports/aged-inventory/vendors', { client_id: clientId }, false, true);
  return res?.vendors ?? [];
}

// Location / category / class options come from the shared client dropdown-options endpoint.
function getDropdownOptions(clientId: string | number): Promise<DropdownOptions> {
  return TopSellersApi.getDropdownOptions(clientId);
}

const AgedInventoryApi = {
  getAgedInventory,
  getVendorOptions,
  getDropdownOptions,
};

export default AgedInventoryApi;
